import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
  Tooltip, CartesianGrid, Legend, Cell,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, TrendingUp, Layers, PieChart as PieIcon, User } from "lucide-react";
import PeriodFilter, { computeRange } from "@/components/PeriodFilter";
import { ACCENT_PALETTE } from "@/lib/constants";

const TOOLTIP_STYLE = { background: "#ffffff", border: "1px solid #ecebe4", borderRadius: 12, color: "#1e1e22", fontSize: 12 };

export default function Analytics() {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const [period, setPeriod] = useState({ key: "quarter", ...computeRange("quarter") });
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [cohorts, setCohorts] = useState(null);
  const [sources, setSources] = useState(null);
  const [members, setMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState("");
  const [memberScorecard, setMemberScorecard] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    const params = {};
    if (period.start) params.start = period.start;
    if (period.end) params.end = period.end;
    api.get("/analytics/overview", { params }).then((r) => setOverview(r.data));
    api.get("/analytics/trends", { params }).then((r) => setTrends(r.data));
    api.get("/analytics/cohorts", { params }).then((r) => setCohorts(r.data));
    api.get("/analytics/sources", { params }).then((r) => setSources(r.data));
    if (isAdmin) api.get("/users").then((r) => setMembers(r.data));
  }, [isAdmin, period.start, period.end]);

  useEffect(() => {
    if (!selectedMember) { setMemberScorecard(null); return; }
    api.get(`/analytics/member/${selectedMember}`).then((r) => setMemberScorecard(r.data));
  }, [selectedMember]);

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/digest/send");
      toast.success(`Weekly digest emailed to ${data.sent} of ${data.attempted} SuperAdmin(s)`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingDigest(false); }
  };

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">Analytics</p>
          <h1 className="text-xl font-semibold mt-1 text-slate-900">Deep Insights</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Trends, cohorts, source ROI and per-member scorecards.
            <span className="ml-2 text-slate-400">· {period.label}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodFilter value={period} onChange={setPeriod} testid="analytics-period" />
          {isAdmin && (
            <Button
              onClick={sendDigest}
              disabled={sendingDigest}
              className="h-9 bg-[#1D63ED] hover:bg-[#1854D9] text-white"
              data-testid="send-digest-btn"
            >
              <FileText className="w-4 h-4 mr-2" /> {sendingDigest ? "Sending…" : "Email digest"}
            </Button>
          )}
        </div>
      </header>

      {/* Top KPIs */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Total Leads" value={overview.total} />
          <KPI label="Won" value={overview.won} sub={`${overview.conversion_rate}% conv.`} tone="emerald" />
          <KPI label="Revenue" value={`₹${(overview.won_value || 0).toLocaleString()}`} tone="emerald" />
          <KPI label="Pipeline" value={`₹${(overview.pipeline_value || 0).toLocaleString()}`} sub={`${overview.total - overview.won - overview.lost} open`} tone="blue" />
        </div>
      )}

      {/* Trend chart */}
      <section className="solid-panel p-6" data-testid="trend-section">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-[#1D63ED]" />
          <h2 className="text-base font-semibold text-slate-900">Trend — {period.label}</h2>
        </div>
        <div className="h-72">
          <ResponsiveContainer>
            <LineChart data={trends?.buckets || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
              <XAxis dataKey="week" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="new" name="New leads" stroke="#1D63ED" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="won" name="Won" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Cohort funnel */}
      <section className="solid-panel p-6" data-testid="cohort-section">
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-4 h-4 text-[#1D63ED]" />
          <h2 className="text-base font-semibold text-slate-900">Cohort funnel — {period.label}</h2>
        </div>
        {!cohorts ? <p className="text-slate-500 text-sm">Loading…</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs">
                <tr>
                  <th className="text-left px-4 py-2">Cohort (week)</th>
                  <th className="text-right px-4 py-2">Total</th>
                  {cohorts.stages.map((s) => (<th key={s} className="text-right px-4 py-2">{s}</th>))}
                  <th className="text-right px-4 py-2">Conv.</th>
                </tr>
              </thead>
              <tbody>
                {cohorts.cohorts.map((c) => {
                  const conv = c.total ? Math.round(((c.stages.Won || 0) / c.total) * 100) : 0;
                  return (
                    <tr key={c.week} className="border-t border-slate-200/60" data-testid={`cohort-row-${c.week}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">{c.week}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{c.total}</td>
                      {cohorts.stages.map((s) => {
                        const n = c.stages[s] || 0;
                        const pct = c.total ? Math.round((n / c.total) * 100) : 0;
                        return (
                          <td key={s} className="px-4 py-3 text-right">
                            <div className="inline-flex flex-col items-end">
                              <span className="text-slate-800 font-medium">{n}</span>
                              <span className="text-[10px] text-slate-500">{pct}%</span>
                            </div>
                          </td>
                        );
                      })}
                      <td className={`px-4 py-3 text-right font-semibold ${conv >= 30 ? "text-emerald-600" : conv >= 10 ? "text-amber-600" : "text-slate-500"}`}>{conv}%</td>
                    </tr>
                  );
                })}
                {cohorts.cohorts.every((c) => c.total === 0) && (
                  <tr><td colSpan={cohorts.stages.length + 3} className="px-4 py-8 text-center text-slate-500 text-sm italic">No cohort data yet. Create some leads to see conversion.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Source ROI */}
      <section className="solid-panel p-6" data-testid="source-roi-section">
        <div className="flex items-center gap-2 mb-4">
          <PieIcon className="w-4 h-4 text-[#1D63ED]" />
          <h2 className="text-base font-semibold text-slate-900">Source ROI</h2>
        </div>
        {!sources ? <p className="text-slate-500 text-sm">Loading…</p> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={sources.sources} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" />
                  <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                  <YAxis dataKey="source" type="category" stroke="#94a3b8" fontSize={11} width={80} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="won_value" name="Revenue" radius={[0, 6, 6, 0]}>
                    {sources.sources.map((s, i) => (
                      <Cell key={s.source} fill={ACCENT_PALETTE[i % ACCENT_PALETTE.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 text-xs">
                  <tr>
                    <th className="text-left px-3 py-2">Source</th>
                    <th className="text-right px-3 py-2">Leads</th>
                    <th className="text-right px-3 py-2">Won</th>
                    <th className="text-right px-3 py-2">Conv.</th>
                    <th className="text-right px-3 py-2">Revenue</th>
                    <th className="text-right px-3 py-2">Avg deal</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.sources.map((s) => (
                    <tr key={s.source} className="border-t border-slate-200/60" data-testid={`source-row-${s.source}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{s.source}</td>
                      <td className="px-3 py-2 text-right text-slate-700">{s.total}</td>
                      <td className="px-3 py-2 text-right text-slate-700">{s.won}</td>
                      <td className="px-3 py-2 text-right text-slate-700">{s.conversion}%</td>
                      <td className="px-3 py-2 text-right font-medium text-emerald-700">₹{Number(s.won_value).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right text-slate-700">{s.avg_deal ? `₹${s.avg_deal.toLocaleString()}` : "—"}</td>
                    </tr>
                  ))}
                  {sources.sources.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500 italic">No source data yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Member scorecards */}
      {isAdmin && (
        <section className="solid-panel p-6" data-testid="member-scorecard-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-[#1D63ED]" />
              <h2 className="text-base font-semibold text-slate-900">Member scorecard</h2>
            </div>
            <Select value={selectedMember} onValueChange={setSelectedMember}>
              <SelectTrigger className="w-56 h-9 bg-white border-slate-200" data-testid="scorecard-select"><SelectValue placeholder="Select a member…" /></SelectTrigger>
              <SelectContent>
                {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!memberScorecard ? (
            <p className="text-sm text-slate-500 italic">Pick a member above to see their scorecard.</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KPI label="Total handled" value={memberScorecard.total} />
                <KPI label="Won" value={memberScorecard.won} sub={`${memberScorecard.conversion}% conv.`} tone="emerald" />
                <KPI label="Revenue" value={`₹${(memberScorecard.won_value || 0).toLocaleString()}`} tone="emerald" />
                <KPI label="Avg deal" value={memberScorecard.avg_deal ? `₹${memberScorecard.avg_deal.toLocaleString()}` : "—"} />
                <KPI label="Pipeline" value={`₹${(memberScorecard.pipeline_value || 0).toLocaleString()}`} sub={`${memberScorecard.open} open`} tone="blue" />
                <KPI label="Avg days to close" value={memberScorecard.avg_days_to_close ?? "—"} />
                <KPI label="Overdue" value={memberScorecard.overdue} tone={memberScorecard.overdue > 0 ? "rose" : "default"} />
                <KPI label="Abandoned" value={memberScorecard.abandoned} tone={memberScorecard.abandoned > 0 ? "amber" : "default"} />
              </div>
              <div className="text-xs text-slate-500">
                Last reminded: {memberScorecard.last_reminded_at ? new Date(memberScorecard.last_reminded_at).toLocaleString() : "never"}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function KPI({ label, value, sub, tone = "default" }) {
  const toneCls = {
    default: "text-slate-900",
    emerald: "text-emerald-700",
    blue: "text-[#1D63ED]",
    rose: "text-rose-700",
    amber: "text-amber-700",
  }[tone];
  return (
    <div className="solid-panel p-4">
      <p className="text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`text-2xl font-semibold mt-1 ${toneCls}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}
